import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Clock, PoundSterling, Building2, MessageSquare, ChevronRight, ChevronDown, Plus, Crown, BookOpen, User, Phone, Mail, MapPin, Award, ExternalLink } from 'lucide-react';

const fmtUK = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };

const API = process.env.REACT_APP_BACKEND_URL;

const CAVEAT = "HMRC Control is a decision-support tool. It does not file returns for you and is not a substitute for advice from an ICAEW / CIOT registered accountant. Always confirm figures before submission.";

const severityColor = { critical: '#DC2626', high: '#D97706', medium: '#3b82f6', low: '#8B7355' };

/* ── Guide Content ── */
const CT_GUIDE = {
  title: "How to Check Corporation Tax Owed",
  steps: [
    { heading: "Log in to Government Gateway", detail: "Go to gov.uk/log-in-register-hmrc-online-services. Select \"Organisation\" (not Individual). Enter your Government Gateway User ID and password." },
    { heading: "Enrol for Corporation Tax (if first time)", detail: "Click \"Add a tax\" or \"Enrol for a service\" and select \"Corporation Tax\". Enter your company's Unique Taxpayer Reference (UTR). HMRC will post an activation code (7–10 working days)." },
    { heading: "View Your Liability", detail: "Once active, your account displays: amount owed (outstanding tax), payment history, filing deadlines, and any interest or penalties applied." },
    { heading: "Payment Deadline", detail: "Corporation Tax is due 9 months and 1 day after the accounting period ends. For example: year-end 31 March 2025 → payment due 1 January 2026." },
    { heading: "Filing Deadline", detail: "Your CT600 Company Tax Return must be filed within 12 months of the accounting period end." },
    { heading: "Current Tax Rates (2025/26 & 2026/27)", detail: "£0–£50,000 profit: 19% (Small Profits Rate). £50,001–£249,999: Marginal Relief (sliding 19%→25%). £250,000+: 25% (Main Rate)." },
    { heading: "Important", detail: "HMRC's free CT600 filing tool is ending in March 2026. You will need commercial software (e.g. TinyTax from £20/yr) or your accountant to file from that date." },
    { heading: "Need Help?", detail: "Corporation Tax Helpline: 0300 200 3410 (Mon–Fri, 8am–6pm). Textphone: 0300 200 3419." },
  ]
};

const VAT_GUIDE = {
  title: "How to Check VAT Balance Owed",
  steps: [
    { heading: "Sign in to your VAT account", detail: "Go to gov.uk/sign-in-vat-account. You need your Government Gateway user ID, password, and your VAT Registration Number (VRN)." },
    { heading: "View Outstanding Liabilities", detail: "Once logged in, look for \"View your payments and how much you owe\" or \"View your payments and outstanding liabilities\". This shows your current VAT balance, payment history, and amounts due." },
    { heading: "Review Past Returns", detail: "Select \"View past returns\" to see figures submitted for each period, the \"Net VAT Payable\" (Box 5), and whether each return is paid or outstanding." },
    { heading: "Making Tax Digital (MTD)", detail: "Ensure you're registered for MTD for VAT. If you see \"You're signed up to Making Tax Digital for VAT\", your account is active and liabilities are visible." },
    { heading: "Payment Updates", detail: "If you recently made a payment, the balance typically updates within 48 hours." },
    { heading: "Alternative: Use Accounting Software", detail: "Software like Xero, QuickBooks, or FreeAgent can generate a VAT Liability Report synced with HMRC data, breaking down taxes owed by rate and period." },
    { heading: "MTD VAT Software Options", detail: "TinyTax: £29/yr per VRN (VAT bridging only). Xero: £15/mo (full accounting). FreeAgent: £14.50/mo (free with NatWest/RBS). GoSimpleTax: £58.70/yr." },
    { heading: "Need Help?", detail: "VAT General Enquiries: 0300 200 3700 (Mon–Fri, 8am–6pm)." },
  ]
};

const KEEPING_ON_TOP = {
  title: "Keeping On Top of Tax — Quick Checklist",
  steps: [
    { heading: "1. Set Up Gateway for Every Company", detail: "Each limited company needs its own HMRC Government Gateway login. You need the UTR and Company Registration Number to enrol." },
    { heading: "2. Check Balances Monthly", detail: "Log in to each company's CT and VAT accounts at least once a month. Note down what's owed and update the tracker in this app." },
    { heading: "3. Know Your Payment Windows", detail: "CT payment: 9 months + 1 day after year-end. VAT payment: 1 month + 7 days after quarter-end. Late payment triggers automatic interest and penalties." },
    { heading: "4. Respond to Debt Letters Promptly", detail: "If HMRC sends a debt letter, respond within 30 days. You can request a Time to Pay (TTP) arrangement by calling 0300 200 3835." },
    { heading: "5. Give Your Accountant Agent Access", detail: "Your accountant (Imran) can be added as an agent on your HMRC accounts. This lets him view and manage filings on your behalf." },
    { heading: "6. Use Software for MTD", detail: "MTD for VAT is already mandatory. MTD for Income Tax starts April 2026 (£50k+). Ensure all VAT-registered companies file digitally." },
    { heading: "7. Keep Records for 6 Years", detail: "HMRC requires you to keep accounting records for at least 6 years from the end of the last accounting period they relate to." },
  ]
};

/* ── Collapsible Guide Component ── */
const GuideCard = ({ guide, icon }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border rounded-sm" style={{ borderColor: '#E5E1DB' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-amber-50/20 transition-colors"
        data-testid={`guide-toggle-${guide.title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-3">
          {icon}
          <h2 className="text-sm font-semibold" style={{ color: '#2C1A0E' }}>{guide.title}</h2>
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: '#8B7355' }} />
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3" style={{ borderTop: '1px solid #F5F1EB' }}>
          {guide.steps.map((step, i) => (
            <div key={i} className="pt-3">
              <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{step.heading}</p>
              <p className="text-xs mt-1 leading-relaxed" style={{ color: '#8B7355' }}>{step.detail}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Advisor Card ── */
const AdvisorCard = ({ advisor }) => (
  <div className="bg-white border rounded-sm p-5" style={{ borderColor: '#E5E1DB' }} data-testid="advisor-card">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#D4AF3718' }}>
        <User className="w-6 h-6" style={{ color: '#D4AF37' }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold" style={{ color: '#2C1A0E' }}>{advisor.name}</h3>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#D4AF3718', color: '#B8860B' }}>{advisor.role}</span>
        </div>
        <p className="text-xs mt-0.5" style={{ color: '#8B7355' }}>{advisor.firm}</p>
        {advisor.qualifications && (
          <div className="flex items-center gap-1.5 mt-2">
            <Award className="w-3 h-3" style={{ color: '#B8860B' }} />
            <p className="text-xs" style={{ color: '#B8860B' }}>{advisor.qualifications}</p>
          </div>
        )}
        <div className="mt-3 space-y-1.5">
          {advisor.email && (
            <div className="flex items-center gap-2">
              <Mail className="w-3 h-3" style={{ color: '#8B7355' }} />
              <a href={`mailto:${advisor.email}`} className="text-xs hover:underline" style={{ color: '#2C1A0E' }}>{advisor.email}</a>
            </div>
          )}
          {advisor.phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-3 h-3" style={{ color: '#8B7355' }} />
              <a href={`tel:${advisor.phone.replace(/\s/g, '')}`} className="text-xs hover:underline" style={{ color: '#2C1A0E' }}>{advisor.phone}</a>
            </div>
          )}
          {advisor.address && (
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3" style={{ color: '#8B7355' }} />
              <p className="text-xs" style={{ color: '#8B7355' }}>{advisor.address}</p>
            </div>
          )}
        </div>
        {advisor.company_number && (
          <a href={`https://find-and-update.company-information.service.gov.uk/company/${advisor.company_number}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs hover:underline" style={{ color: '#B8860B' }}>
            <ExternalLink className="w-3 h-3" /> Companies House #{advisor.company_number}
          </a>
        )}
        {advisor.companies?.length > 0 && (
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #F5F1EB' }}>
            <p className="text-xs font-medium mb-1.5" style={{ color: '#8B7355' }}>Covers:</p>
            <div className="flex flex-wrap gap-1.5">
              {advisor.companies.map((c, i) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded-sm" style={{ backgroundColor: '#FAF7F2', color: '#2C1A0E', border: '1px solid #E5E1DB' }}>{c}</span>
              ))}
            </div>
          </div>
        )}
        {advisor.notes && (
          <p className="text-xs mt-2 italic" style={{ color: '#8B7355' }}>{advisor.notes}</p>
        )}
      </div>
    </div>
  </div>
);

const HmrcDashboard = () => {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState([]);
  const [debtSummary, setDebtSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'guides'

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/hmrc/deadlines/upcoming`, { withCredentials: true }),
      axios.get(`${API}/api/hmrc/debts/summary`, { withCredentials: true }),
      axios.get(`${API}/api/hmrc/companies`, { withCredentials: true }),
      axios.get(`${API}/api/hmrc/advisors`, { withCredentials: true }),
    ]).then(([dl, ds, co, ad]) => {
      setDeadlines(dl.data.slice(0, 5));
      setDebtSummary(ds.data);
      setCompanies(co.data);
      setAdvisors(ad.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const daysUntil = (dateStr) => Math.ceil((new Date(dateStr) - new Date()) / 86400000);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#D4AF37' }} />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="hmrc-dashboard">
      {/* Caveat Banner */}
      <div className="px-6 py-2" style={{ backgroundColor: '#FAF7F2', borderBottom: '1px solid #E8E0D4' }}>
        <p className="text-xs text-center" style={{ color: '#8B7355' }}>{CAVEAT}</p>
      </div>

      {/* Header */}
      <header className="bg-white px-6 lg:px-10 py-6" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4AF3718' }}>
              <Crown className="w-5 h-5" style={{ color: '#D4AF37' }} />
            </div>
            <div>
              <h1 className="text-2xl font-light tracking-tight" style={{ color: '#2C1A0E' }}>HMRC Control</h1>
              <p className="text-xs" style={{ color: '#8B7355' }}>Tax compliance & debt management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/hmrc/chat')} className="flex items-center gap-2 px-4 py-2 text-xs font-medium border rounded-sm transition-all hover:shadow-sm" style={{ borderColor: '#E5E1DB', color: '#8B7355' }} data-testid="ask-sterling-btn">
              <MessageSquare className="w-3.5 h-3.5" /> Ask Sterling
            </button>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 text-xs font-medium rounded-sm" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="back-to-ops">
              Ops Suite
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6">
        <div className="flex gap-1 p-1 rounded-sm" style={{ backgroundColor: '#F5F1EB' }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'guides', label: 'HMRC Guides' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex-1 px-4 py-2 text-xs font-medium rounded-sm transition-all"
              style={{
                backgroundColor: activeTab === tab.key ? '#FFFFFF' : 'transparent',
                color: activeTab === tab.key ? '#2C1A0E' : '#8B7355',
                boxShadow: activeTab === tab.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-6 space-y-6">

        {activeTab === 'overview' && (
          <>
            {/* Debt Alert Banner */}
            {debtSummary && debtSummary.with_collector_count > 0 && (
              <div className="p-4 flex items-center gap-3 rounded-sm" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }} data-testid="collector-alert">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" style={{ color: '#DC2626' }} />
                <p className="text-sm font-medium" style={{ color: '#8B7355' }}>
                  {debtSummary.with_collector_count} debt{debtSummary.with_collector_count > 1 ? 's' : ''} with private collectors — £{debtSummary.with_collector_amount.toLocaleString('en-GB', { minimumFractionDigits: 2 })} outstanding. Action required.
                </p>
              </div>
            )}

            {/* KPI Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-5 rounded-sm border" style={{ borderColor: '#E5E1DB' }} data-testid="kpi-next-deadline">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4" style={{ color: '#8B7355' }} />
                  <span className="text-xs uppercase tracking-widest font-medium" style={{ color: '#8B7355' }}>Next Deadline</span>
                </div>
                {deadlines[0] ? (
                  <>
                    <p className="text-2xl font-light" style={{ color: '#2C1A0E' }}>{daysUntil(deadlines[0].due_date)} days</p>
                    <p className="text-xs mt-1" style={{ color: '#8B7355' }}>{deadlines[0].title}</p>
                    <p className="text-xs mt-0.5 font-mono" style={{ color: severityColor[deadlines[0].severity] }}>{fmtUK(deadlines[0].due_date)}</p>
                  </>
                ) : <p className="text-sm" style={{ color: '#8B7355' }}>No upcoming deadlines</p>}
              </div>

              <div className="bg-white p-5 rounded-sm border" style={{ borderColor: '#E5E1DB' }} data-testid="kpi-total-debt">
                <div className="flex items-center gap-2 mb-3">
                  <PoundSterling className="w-4 h-4" style={{ color: '#8B7355' }} />
                  <span className="text-xs uppercase tracking-widest font-medium" style={{ color: '#8B7355' }}>HMRC Debt</span>
                </div>
                <p className="text-2xl font-light" style={{ color: debtSummary?.total_outstanding > 0 ? '#DC2626' : '#16A34A' }}>
                  £{(debtSummary?.total_outstanding || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs mt-1" style={{ color: '#8B7355' }}>{debtSummary?.count || 0} active debts</p>
              </div>

              <div className="bg-white p-5 rounded-sm border" style={{ borderColor: '#E5E1DB' }} data-testid="kpi-companies">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4" style={{ color: '#8B7355' }} />
                  <span className="text-xs uppercase tracking-widest font-medium" style={{ color: '#8B7355' }}>Companies</span>
                </div>
                <p className="text-2xl font-light" style={{ color: '#2C1A0E' }}>{companies.length}</p>
                <p className="text-xs mt-1" style={{ color: '#8B7355' }}>Tracked in HMRC Control</p>
              </div>
            </div>

            {/* Deadlines List */}
            <div className="bg-white border rounded-sm" style={{ borderColor: '#E5E1DB' }}>
              <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#B8860B' }}>Upcoming Deadlines</h2>
              </div>
              {deadlines.map((d, i) => {
                const days = daysUntil(d.due_date);
                return (
                  <div key={d.id || i} className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #F5F1EB' }} data-testid={`deadline-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: severityColor[d.severity] }} />
                      <div>
                        <p className="text-sm" style={{ color: '#2C1A0E' }}>{d.title}</p>
                        <p className="text-xs font-mono" style={{ color: '#8B7355' }}>{fmtUK(d.due_date)}</p>
                      </div>
                    </div>
                    <span className="text-xs font-medium px-2 py-1 rounded-sm font-mono" style={{
                      color: days <= 7 ? '#DC2626' : days <= 30 ? '#D97706' : '#16A34A',
                      backgroundColor: days <= 7 ? '#FEF2F2' : days <= 30 ? '#FFFBEB' : '#F0FDF4',
                    }}>{days}d</span>
                  </div>
                );
              })}
              {deadlines.length === 0 && (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm" style={{ color: '#8B7355' }}>No companies added yet. Add a company to generate deadlines.</p>
                </div>
              )}
            </div>

            {/* Companies List */}
            <div className="bg-white border rounded-sm" style={{ borderColor: '#E5E1DB' }}>
              <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB' }}>
                <h2 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#B8860B' }}>Your Companies</h2>
                <button onClick={() => navigate('/hmrc/companies')} className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-sm" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }} data-testid="manage-companies-btn">
                  <Plus className="w-3 h-3" /> Manage
                </button>
              </div>
              {companies.map((c) => (
                <button key={c.id} onClick={() => navigate(`/hmrc/companies/${c.id}`)} className="w-full px-5 py-3 flex items-center justify-between hover:bg-amber-50/30 transition-colors text-left" style={{ borderBottom: '1px solid #F5F1EB' }} data-testid={`company-row-${c.id}`}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{c.name}</p>
                    <p className="text-xs font-mono" style={{ color: '#8B7355' }}>
                      {c.company_number ? `#${c.company_number}` : ''}{c.utr ? ` · UTR ${c.utr}` : ''}{c.vat_number ? ` · VAT ${c.vat_number}` : ''}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4" style={{ color: '#8B7355' }} />
                </button>
              ))}
              {companies.length === 0 && (
                <div className="px-5 py-8 text-center">
                  <p className="text-sm mb-3" style={{ color: '#8B7355' }}>No companies added yet</p>
                  <button onClick={() => navigate('/hmrc/companies')} className="text-xs font-medium px-4 py-2 rounded-sm" style={{ backgroundColor: '#2C1A0E', color: '#D4AF37' }}>Add First Company</button>
                </div>
              )}
            </div>

            {/* Your Accountant */}
            {advisors.length > 0 && (
              <div>
                <h2 className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: '#B8860B' }}>Your Accountant</h2>
                <div className="space-y-3">
                  {advisors.map(a => <AdvisorCard key={a.id} advisor={a} />)}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'guides' && (
          <div className="space-y-4">
            <div className="p-4 rounded-sm" style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8E0D4' }}>
              <p className="text-sm" style={{ color: '#2C1A0E' }}>
                Step-by-step reference for checking what your companies owe HMRC.
                Use these guides alongside your Government Gateway logins.
              </p>
            </div>

            <GuideCard
              guide={CT_GUIDE}
              icon={<PoundSterling className="w-4 h-4" style={{ color: '#B8860B' }} />}
            />
            <GuideCard
              guide={VAT_GUIDE}
              icon={<PoundSterling className="w-4 h-4" style={{ color: '#B8860B' }} />}
            />
            <GuideCard
              guide={KEEPING_ON_TOP}
              icon={<BookOpen className="w-4 h-4" style={{ color: '#B8860B' }} />}
            />

            {/* Quick Links */}
            <div className="bg-white border rounded-sm p-5" style={{ borderColor: '#E5E1DB' }}>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: '#B8860B' }}>Quick Links</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { label: 'HMRC Government Gateway Login', url: 'https://www.gov.uk/log-in-register-hmrc-online-services' },
                  { label: 'Sign in to VAT Account', url: 'https://www.gov.uk/sign-in-vat-account' },
                  { label: 'Pay Corporation Tax', url: 'https://www.gov.uk/pay-corporation-tax' },
                  { label: 'Pay VAT', url: 'https://www.gov.uk/pay-vat' },
                  { label: 'Request Time to Pay', url: 'https://www.gov.uk/difficulties-paying-hmrc' },
                  { label: 'Companies House Search', url: 'https://find-and-update.company-information.service.gov.uk/' },
                  { label: 'MTD Software List', url: 'https://www.gov.uk/guidance/find-software-thats-compatible-with-making-tax-digital-for-vat' },
                  { label: 'CT Helpline: 0300 200 3410', url: 'tel:03002003410' },
                ].map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2.5 rounded-sm text-xs hover:shadow-sm transition-all" style={{ backgroundColor: '#FAF7F2', color: '#2C1A0E', border: '1px solid #E8E0D4' }} data-testid={`quick-link-${i}`}>
                    <ExternalLink className="w-3 h-3 flex-shrink-0" style={{ color: '#B8860B' }} />
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default HmrcDashboard;
